/* Counselors — read-only directory of advisors assigned to the student.
 * Falls back to a friendly default pair (academic + finance) when no
 * explicit assignments exist, so the page never reads empty. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, Mail, Phone, Clock } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

function initials(name) {
  return (name || '')
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Counselors() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/academy/my/counselors')
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">My Counselors</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Your advisors are here to help — reach out any time.</p>

        {loading ? (
          <div className="text-[#6E6E73] text-[14px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center">
            <Users className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
            <div className="text-[16px] font-bold text-[#1D1D1F]">No counselors assigned yet</div>
            <p className="text-[13.5px] text-[#6E6E73]">Once you're enrolled, your advisor team will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rows.map((c) => (
              <article key={c.id || c.email} data-testid={`counselor-${c.id || c.email}`}
                className="rounded-2xl border border-[#E5E5E7] bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#c9a227]/50 hover:shadow-[0_10px_30px_-15px_rgba(201,162,39,0.25)]">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-[15px] font-extrabold text-[#0e0e10] tracking-wider"
                    style={{ background: 'linear-gradient(135deg,#fff8e7 0%,#ffe9a3 100%)' }}>
                    {initials(c.name)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">{c.role}</div>
                    <div className="text-[18px] font-extrabold text-[#0e0e10] leading-tight mt-0.5">{c.name}</div>
                    {c.bio && <p className="text-[13px] text-[#3a3a3f] mt-2 leading-relaxed">{c.bio}</p>}
                  </div>
                </div>

                <dl className="mt-5 space-y-2 text-[13px]">
                  {c.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-[#c9a227]" />
                      <a href={`mailto:${c.email}`} className="text-[#0e0e10] hover:text-[#a8871a] font-mono text-[12.5px]">
                        {c.email}
                      </a>
                    </div>
                  )}
                  {c.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-3.5 h-3.5 text-[#c9a227]" />
                      <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`} className="text-[#0e0e10] hover:text-[#a8871a] font-mono text-[12.5px]">
                        {c.phone}
                      </a>
                    </div>
                  )}
                  {c.office_hours && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-[#c9a227]" />
                      <span className="text-[#3a3a3f] text-[12.5px]">{c.office_hours}</span>
                    </div>
                  )}
                </dl>

                <div className="mt-5 flex flex-wrap gap-2">
                  {c.email && (
                    <a href={`mailto:${c.email}?subject=Hi%20${encodeURIComponent(c.name || 'there')}`}
                      data-testid={`counselor-email-${c.id || c.email}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
                      style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
                      <Mail className="w-3 h-3" /> Send email
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}
                      data-testid={`counselor-call-${c.id || c.email}`}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider border border-[#0e0e10] text-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors">
                      <Phone className="w-3 h-3" /> Call
                    </a>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
